import { IDLE_RESTART_NAME } from '@/services/idle-restart/idle-restart.utils'
import { MY_PR_LOOP_NAME } from '@/services/my-pr-loop/my-pr-loop.utils'
import { formatRelativeTime } from '@/utils/dateUtils'
import { getErrorMessage, getErrorStatus, isApiError } from '@/utils/error-handler'
import type {
  ActionChoices,
  ActionOffer,
  ActionRequest,
  ActionRequestState,
  DeployState,
  MachineTally,
  MachineThanks,
  MyPr,
  MyPrBuild,
  MyTicket,
  MyWork,
  MyWorkCap,
  StartActionRequest
} from './my-work.types'
import type { MySweep } from './my-work-sweep.types'
import type { FleetHeadsUp } from '@/services/claude-fleet/claude-fleet.requests.types'

export const MY_WORK_ACTIONS_POLL_MS = 10_000
export const MY_WORK_RECENT_LIMIT = 20

export type MyWorkPillColor = 'green' | 'blue' | 'red' | 'gray' | 'yellow'

export interface MyWorkPill {
  label: string
  color: MyWorkPillColor
}

export interface TicketStatusGroup {
  status: string
  tickets: MyTicket[]
}

/** Work in motion first, then work waiting on someone, then the backlog. */
export const TICKET_STATUS_ORDER = [
  'In Progress',
  'Agent In Progress',
  'Failed QA',
  'PR Review',
  'Ready for Testing',
  'Testing',
  'Done',
  'Done in Staging',
  'To Do',
  'Paused'
]

/** Groups tickets by status in workflow order; a status outside it follows, in the order it first appears. */
export function groupTicketsByStatus(tickets: MyTicket[]): TicketStatusGroup[] {
  const groups = new Map<string, TicketStatusGroup>()
  tickets.forEach(ticket => {
    const group = groups.get(ticket.status) ?? { status: ticket.status, tickets: [] }
    group.tickets.push(ticket)
    groups.set(ticket.status, group)
  })
  return [...groups.values()].sort(
    (left, right) => statusRank(left.status) - statusRank(right.status)
  )
}

function statusRank(status: string): number {
  const rank = TICKET_STATUS_ORDER.findIndex(known => known.toLowerCase() === status.toLowerCase())
  return rank === -1 ? TICKET_STATUS_ORDER.length : rank
}

const BUILD_PILLS: Record<MyPrBuild, MyWorkPill> = {
  green: { label: 'Passed', color: 'green' },
  red: { label: 'Failed', color: 'red' },
  running: { label: 'Running', color: 'blue' },
  'approval-gate': { label: 'Waiting for approval', color: 'yellow' },
  none: { label: 'No build', color: 'gray' },
  'no-ci': { label: 'No CI', color: 'gray' },
  unknown: { label: 'Unknown', color: 'gray' }
}

export function buildPill(build: MyPrBuild | undefined): MyWorkPill {
  return BUILD_PILLS[build ?? 'unknown'] ?? BUILD_PILLS.unknown
}

const DEPLOY_COLORS: Record<DeployState, MyWorkPillColor> = {
  verified: 'green',
  deployed: 'blue',
  waiting: 'gray',
  'cant-verify': 'yellow',
  staged: 'yellow',
  unknown: 'gray'
}

export function deployColor(state: DeployState | undefined): MyWorkPillColor {
  return DEPLOY_COLORS[state ?? 'unknown'] ?? 'gray'
}

const REQUEST_PILLS: Record<ActionRequestState, MyWorkPill> = {
  queued: { label: 'Queued', color: 'gray' },
  running: { label: 'Running', color: 'blue' },
  done: { label: 'Done', color: 'green' }
}

export function requestPill(state: ActionRequestState): MyWorkPill {
  return REQUEST_PILLS[state] ?? REQUEST_PILLS.queued
}

/** While any request is queued or running, the page keeps asking the fleet how it is going. */
export function hasPendingRequests(requests: ActionRequest[]): boolean {
  return requests.some(request => request.state !== 'done')
}

const STATE_PROGRESS: Record<ActionRequestState, number> = { queued: 0, running: 1, done: 2 }

/**
 * One list of requests from two answers, newest first. A request in both keeps whichever copy has
 * got further, because a slower answer can carry an older state. Nothing is cut: past 20 the fleet
 * sends only requests that count against today, and the cap's hover lists each of them.
 */
export function mergeActionRequests(
  latest: ActionRequest[],
  earlier: ActionRequest[]
): ActionRequest[] {
  const byId = new Map<string, ActionRequest>()
  ;[...earlier, ...latest].forEach(request => {
    const known = byId.get(request.id)
    if (!known || STATE_PROGRESS[request.state] >= STATE_PROGRESS[known.state])
      byId.set(request.id, request)
  })
  return [...byId.values()]
    .sort((left, right) => Date.parse(right.at) - Date.parse(left.at))
}

/** Recent actions shows only the latest requests. */
export function recentActionRequests(requests: ActionRequest[]): ActionRequest[] {
  return requests.slice(0, MY_WORK_RECENT_LIMIT)
}

/** What the header says about today's actions; empty for an operator, who has no cap. */
export function capSummary(cap: MyWorkCap): string {
  if (cap.unlimited) return ''
  if (cap.perDay === 0) return 'Starting Claude from here is turned off'
  return `${cap.remaining} / ${cap.perDay} actions left today`
}

export interface CapUse {
  label: string
  target: string
  at: string
}

/** What used today's actions, newest first: the recent requests the fleet marked, and today's sweep. */
export function capUses(requests: ActionRequest[], sweep: MySweep | null | undefined): CapUse[] {
  const uses: CapUse[] = requests
    .filter(request => request.countsToday)
    .map(({ label, target, at }) => ({ label, target, at }))
  if (sweep?.countsToday) uses.push({ label: 'Run my sweep', target: 'your work', at: sweep.requestedAt })
  return uses.sort((left, right) => Date.parse(right.at) - Date.parse(left.at))
}

/** The fleet lists only its latest requests, so an older use of today's actions may not be among them. */
export function capUsesNotListed(cap: MyWorkCap, uses: CapUse[]): number {
  return Math.max(0, cap.used - uses.length)
}

export function capUseTime(at: string): string {
  const date = new Date(at)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export function prLabel(pr: Pick<MyPr, 'repo' | 'id'>): string {
  return `${pr.repo} #${pr.id}`
}

/** How a PR is named when someone asks Claude about it: the label, then its title. */
export function prAskTitle(pr: Pick<MyPr, 'repo' | 'id' | 'title'>): string {
  return `${prLabel(pr)}: ${pr.title}`
}

export function prKey(pr: Pick<MyPr, 'repo' | 'id'>): string {
  return `${pr.repo}-${pr.id}`
}

export function prTarget(pr: Pick<MyPr, 'repo' | 'id'>): string {
  return `${pr.repo}#${pr.id}`
}

/** A count the fleet could not read is null, and shows as a dash rather than a misleading 0. */
export function formatCount(count: number | null | undefined): string {
  return typeof count === 'number' ? String(count) : '—'
}

/**
 * Claude's open findings by severity, ["0 blocking", "0 non-blocking"], plus other people's unanswered threads
 * when there are some. One line each, so a narrow column never cuts a count off. Facts read before findings
 * were split by severity give the thread count.
 */
export function findingsLines(pr: Pick<MyPr, 'claudeFindings' | 'openFindings'>): string[] {
  const claude = pr.claudeFindings
  if (!claude) {
    return [typeof pr.openFindings === 'number' ? `${pr.openFindings} open findings` : 'findings not read yet']
  }
  const lines = [`${claude.blocking} blocking`, `${claude.nonBlocking} non-blocking`]
  const others =
    typeof pr.openFindings === 'number'
      ? pr.openFindings - (claude.blocking + claude.nonBlocking - claude.waiting)
      : 0
  if (others > 0) lines.push(`${others} other open ${others === 1 ? 'thread' : 'threads'}`)
  return lines
}

/** The findings lines on one line, "0 blocking · 0 non-blocking", for places with room to run across. */
export function findingsSummary(pr: Pick<MyPr, 'claudeFindings' | 'openFindings'>): string {
  return findingsLines(pr).join(' · ')
}

export function formatBehind(pr: Pick<MyPr, 'behind' | 'behindMore'>): string {
  if (typeof pr.behind !== 'number') return '—'
  return pr.behindMore ? `${pr.behind}+` : String(pr.behind)
}

/** Deliver opens a PR and Nudge reviewer announces one, so each posts a squad heads-up. */
const HEADS_UP_ACTIONS = new Set(['ticket_deliver', 'pr_announce'])

/** Nudge reviewer exists to announce, so the fleet refuses Don't announce for it, whether picked or saved. */
const announces = (offer: ActionOffer): boolean => offer.action === 'pr_announce'

/**
 * Whether starting this offer must ask how its PR heads-up posts: the person has not chosen yet (mode null),
 * or chose Don't announce and is nudging reviewers.
 */
export function asksHeadsUp(offer: ActionOffer, headsUp: Pick<FleetHeadsUp, 'mode'> | undefined): boolean {
  if (!HEADS_UP_ACTIONS.has(offer.action) || !headsUp) return false
  return headsUp.mode === null || (announces(offer) && headsUp.mode === 'off')
}

/** The ways this offer's heads-up can post: every mode the fleet lists, less Don't announce for Nudge reviewer. */
export function headsUpChoices(offer: ActionOffer, modes: FleetHeadsUp['modes']): FleetHeadsUp['modes'] {
  return announces(offer) ? modes.filter(mode => mode.value !== 'off') : modes
}

/** An offer the person has to make a choice on opens the action dialog instead of a plain confirm. */
export function needsChoices(offer: ActionOffer, asksHeadsUpChoice = false): boolean {
  return Boolean(offer.envChoices?.length) || Boolean(offer.deepChoice) || asksHeadsUpChoice
}

/** The POST body: a fixed env wins, a picked one is sent only when the offer asks, deep only when offered. */
export function actionRequestBody(
  offer: ActionOffer,
  target: string,
  choices: ActionChoices
): StartActionRequest {
  const env =
    offer.env ?? (offer.envChoices?.length ? (choices.env ?? offer.envChoices[0]) : undefined)
  return {
    action: offer.action,
    target,
    ...(env ? { env } : {}),
    ...(offer.deepChoice && choices.deep ? { deep: true } : {}),
    ...(choices.headsUp ? { headsUp: choices.headsUp, headsUpRemember: choices.headsUpRemember !== false } : {}),
    ...(choices.runOn ? { runOn: choices.runOn } : {})
  }
}

/** The fleet's own `{error}` sentence, which is written to be shown as-is and is kept for a 403 too. */
/** The fleet API's sign-in check refused this person as not staff (403, "the fleet is for Tattle staff").
 *  Retrying cannot help, so the page says who it is for instead of offering Try again. Other 403s carry
 *  their own reason (no Jira user, an email Okta does not tie to them) and keep the error state. */
export function isStaffOnlyRefusal(failure: unknown): boolean {
  if (getErrorStatus(failure) !== 403 || !isApiError(failure)) return false
  const text = failure.response?.data?.error
  return typeof text === 'string' && /for Tattle staff/i.test(text)
}

export const STAFF_ONLY_BODY =
  'This page is for Tattle staff signed in through Okta. If you are staff and see this, ask a fleet operator to check your access.'

const SERVER_FAILED = 'Something went wrong on our side. Try again in a minute.'

/** The server's own text, for a 4xx refusal (a reason written to be read) or kept back for details. */
function serverText(failure: unknown): string {
  if (!isApiError(failure)) return ''
  const text = failure.response?.data?.error
  return typeof text === 'string' ? text.trim() : ''
}

/** Text written for people: a capital letter, at least three words, and not axios's own wording. */
function isSentence(text: string): boolean {
  return /^[A-Z]/.test(text) && text.trim().split(/\s+/).length >= 3 && !/^Request failed with status code/i.test(text)
}

/** What to show when a fleet read fails. A 4xx carries the fleet's own sentence, written to be shown;
 *  a 5xx body is whatever the server threw ("boom"), so the page says what happened in plain words and
 *  myWorkErrorDetail keeps the raw text for a collapsed details line. No response at all is `fallback`. */
export function myWorkErrorText(failure: unknown, fallback: string): string {
  const status = getErrorStatus(failure)
  if (status && status >= 500) {
    // The fleet writes some 5xx answers as sentences meant for people ("Something went wrong on our
    // side"); a bare exception text ("boom") or axios's "Request failed with status code 500" is not one.
    const message = (failure as { message?: string }).message ?? ''
    return [serverText(failure), message].find(isSentence) ?? SERVER_FAILED
  }
  const text = serverText(failure)
  if (text) return text
  if (status && status >= 400) return getErrorMessage(failure, fallback) || fallback
  return fallback
}

/** The raw detail behind a failure the page reworded: the server's text or the error message. */
export function myWorkErrorDetail(failure: unknown): string {
  const status = getErrorStatus(failure)
  if (status && status >= 400 && status < 500) return ''
  if (isSentence(serverText(failure))) return ''
  const text = serverText(failure) || (failure instanceof Error ? failure.message : '')
  return status ? `${status}${text ? `: ${text}` : ''}` : text
}

const FAILURE_NOTE = /could not|couldn't|failed|unavailable|error|timed out/i

/** The element id of a status group in My tickets, for the page's On this page list. */
export const ticketGroupId = (status: string): string =>
  `my-work-tickets-group-${status.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`

/** My work's On this page list, each with how many rows it shows; `qaCount` is absent until that list is read. */
/**
 * My work's On this page list: each item is labelled with its section's title, and a section that renders
 * nothing (no open decisions, no sweep offered) is left out.
 */
export function myWorkSections(
  work: MyWork | undefined,
  qaCount: number | undefined,
  openDecisions = work?.decisions.length ?? 0,
  ticketGroups = groupTicketsByStatus(work?.tickets ?? [])
) {
  if (!work) return []
  const groups = ticketGroups.map(group => ({
    id: ticketGroupId(group.status),
    label: group.status,
    count: group.tickets.length,
    nested: true
  }))
  return [
    ...(openDecisions ? [{ id: 'my-work-decisions', label: 'Decisions waiting on me', count: openDecisions }] : []),
    { id: 'my-work-review-requested', label: 'Review requested', count: work.reviewRequested.length },
    { id: 'my-work-qa', label: 'My tickets in QA', count: qaCount },
    { id: 'my-work-tickets', label: 'My tickets', count: work.tickets.length },
    ...groups,
    { id: 'my-work-my-prs', label: 'My open PRs', count: work.myPrs.length },
    { id: 'my-work-repo-prs', label: 'PRs in my repos' },
    { id: 'my-work-claude-on-my-prs', label: 'Claude on my PRs' },
    { id: 'my-work-review-when-asked', label: "Review PRs I'm asked to review" },
    { id: 'my-work-heads-ups', label: 'PR heads-ups' },
    { id: 'my-work-open-in-claude-code', label: 'Open in Claude Code' },
    { id: 'my-work-my-runner', label: 'My runner' },
    { id: 'my-work-pr-loop', label: MY_PR_LOOP_NAME },
    { id: 'my-work-idle-restart', label: IDLE_RESTART_NAME },
    { id: 'my-work-notifications', label: 'Notifications' },
    { id: 'my-work-job-alerts', label: 'Fleet job alerts' },
    ...(work.sweep !== undefined ? [{ id: 'my-work-sweep', label: 'My sweep' }] : []),
    { id: 'my-work-recent', label: 'Recent actions', count: work.recent.length },
    { id: 'my-work-team', label: 'Team view' }
  ]
}

/** A fleet older than per-row pending details explains a Bitbucket shortfall in its notes instead. */
const SHORTFALL_NOTE = /Bitbucket budget|asked the fleet to slow down/i

export const isShortfallNote = (note: string): boolean => SHORTFALL_NOTE.test(note)

/** The notes the page shows: a Bitbucket shortfall is shown on the PR rows it affects, not here. */
export const shownNotes = (notes: string[]): string[] => notes.filter(note => !isShortfallNote(note))

/** Whether some PR details are still to come, from this fleet's flag or an older fleet's note. */
export const hasPendingDetails = (work: Pick<MyWork, 'partial' | 'notes'> | undefined): boolean =>
  Boolean(work && (work.partial || work.notes.some(isShortfallNote)))

/**
 * How long after a short read the page asks again; after the last it stops and says so. One retry:
 * each read of /fleet/me costs the fleet several seconds, and the fleet fills PR details from its
 * own webhook-fed cache, so asking more often mostly repeats the same answer.
 */
export const PENDING_DETAILS_RETRY_MS = [60_000]

export const PENDING_DETAILS_STILL_MISSING =
  'Some PR details are still loading from Bitbucket. They fill in on the next refresh.'

/** The heading over the fleet's notes: a failure heading only when a note says something failed;
 *  otherwise the notes are information (the test reader's note, for one). */
export const ABOUT_THIS_PAGE = 'About this page'

export function notesHeading(notes: string[], failureTitle: string): string {
  return notes.some(note => FAILURE_NOTE.test(note)) ? failureTitle : ABOUT_THIS_PAGE
}

const TALLY_WORDS: [keyof Omit<MachineTally, 'total' | 'savedUsd'>, string, string][] = [
  ['reviews', 'review', 'reviews'],
  ['fixes', 'fix', 'fixes'],
  ['qa', 'QA check', 'QA checks'],
  ['mentions', 'mention', 'mentions'],
  ['releaseChecks', 'release check', 'release checks'],
  ['lanes', 'delivery tick', 'delivery ticks']
]

/** "3 reviews, 1 fix" — the kinds that ran, most first; "nothing yet" when none did. */
export function tallyKinds(tally: MachineTally): string {
  const parts = TALLY_WORDS.filter(([key]) => tally[key] > 0)
    .sort((a, b) => tally[b[0]] - tally[a[0]])
    .map(([key, one, many]) => `${tally[key]} ${tally[key] === 1 ? one : many}`)
  return parts.length ? parts.join(', ') : 'nothing yet'
}

/** The quiet "Your machine" line: today and this week, and the AgentCore cost the laptop spared. */
export function machineStatsLine(thanks: Pick<MachineThanks, 'today' | 'week'>): string {
  const saved = thanks.week.savedUsd > 0 ? ` · about $${thanks.week.savedUsd.toFixed(2)} of AgentCore saved this week` : ''
  return `Today: ${tallyKinds(thanks.today)}. This week: ${tallyKinds(thanks.week)}${saved}.`
}

/**
 * The header's freshness line, from when the fleet produced the answer on screen (not when it
 * arrived): "Updated 4 minutes ago (cached) · refreshing…" while a newer read is on its way.
 */
export function myWorkUpdatedText(
  work: Pick<MyWork, 'generatedAt' | 'cached' | 'stale'> | undefined,
  { now, reading }: { now: Date; reading: boolean }
): string {
  if (!work?.generatedAt) return ''
  const cached = work.cached || work.stale ? ' (cached)' : ''
  const refreshing = reading ? ' · refreshing…' : ''
  return `Updated ${formatRelativeTime(work.generatedAt, now).toLowerCase()}${cached}${refreshing}`
}
